// Common date/time formatting utilities for messaging

/**
 * Formats a timestamp (ISO string) to a locale string.
 */
export const formatTimestamp = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString();
};

/**
 * Groups an array of messages by calendar date (YYYY-M-D).
 * Returns an object where keys are date strings, and values are arrays of messages.
 */
export function groupMessagesByDate<T extends { sentDate: string }>(messages: T[]): Record<string, T[]> {
  const grouped: Record<string, T[]> = {};
  messages.forEach((message) => {
    const date = new Date(message.sentDate);
    const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(message);
  });
  return grouped;
}

/**
 * Formats a date header for display above grouped messages.
 * Returns an object with a 'text' (date only) and 'date' (time only) property.
 */
export function formatDateHeader(dateString: string): { text: string; date: string } {
  const date = new Date(dateString);
  return { text: date.toLocaleDateString(), date: date.toLocaleTimeString() };
}

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}